/**
 * @fileoverview Input/output schemas for short-story-planner-v4.
 *
 * This agent takes a plot concept and expands it into a written plot outline,
 * plot threads, and suggested characters for short stories.
 *
 * Key difference from v3: No storylines or beats. The agent outputs plot threads
 * directly (title, summary, threadQuestion) instead of storylines with beats
 * and story questions. The writtenPlot is the single source of truth for the
 * story's structural arc.
 *
 * Output schema is flat (3 levels max) for Gemini API compatibility.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { CharacterV2IdeaSchema } from '@/domains/characters/schemas';
import { PacingEnum, ChapterGoalsSchema } from '@/domains/chapter-workspace/schemas';

// =============================================================================
// SOURCE MATERIAL SCHEMAS (reused from v3)
// =============================================================================

/**
 * Schema for source materials as provided by UI/hooks (with type discriminator).
 */
export const SourceMaterialSchema = z.object({
  type: z.enum(['sequel', 'inspiration']).describe('Whether this is a sequel source or inspiration source.'),
  content: z.string().describe('The full text content of the source story.'),
});

export type SourceMaterial = z.infer<typeof SourceMaterialSchema>;

/**
 * Schema for source materials as passed to the flow (with pre-computed header).
 * This keeps logic in hooks, not in Handlebars templates.
 */
export const FlowSourceMaterialSchema = z.object({
  header: z.string().describe('Pre-computed header for this source material (e.g., "Original Story (for sequel/spin-off)").'),
  content: z.string().describe('The full text content of the source story.'),
});

export type FlowSourceMaterial = z.infer<typeof FlowSourceMaterialSchema>;

/**
 * Transforms a SourceMaterial to a FlowSourceMaterial by computing the header.
 */
export function transformSourceMaterial(material: SourceMaterial): FlowSourceMaterial {
  const header = material.type === 'sequel'
    ? 'Original Story (for sequel/spin-off)'
    : 'Inspiration Source';
  return { header, content: material.content };
}

// =============================================================================
// HOOK INPUT SCHEMA
// =============================================================================

export const HookInputSchema = z.object({
  userMessage: z.string().describe('User instructions including the plot concept to expand.'),
  sourceMaterials: z.array(SourceMaterialSchema).optional().describe('Source stories for sequel/inspiration-based plots.'),
});

export type HookInput = z.infer<typeof HookInputSchema>;

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.storyContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.loreContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.seriesContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.characters.schema)
  .merge(AGENT_SCHEMA_OBJECTS.worldElements.schema)
  .merge(AGENT_SCHEMA_OBJECTS.userMessage.schema)
  .merge(AGENT_SCHEMA_OBJECTS.planningGenreGuidance.schema)
  .merge(AGENT_SCHEMA_OBJECTS.bannedNames.schema)
  .merge(AGENT_SCHEMA_OBJECTS.projectedWordCount.schema)
  .merge(z.object({
    sourceMaterials: z.array(FlowSourceMaterialSchema).optional().describe('Source stories for sequel/inspiration-based plots with pre-computed headers.'),
  }));

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA (Flat structure for Gemini compatibility)
// =============================================================================

/**
 * Agent output for a character arc — short-form only (no beats).
 * The hook matches these to suggestedCharacters by name and handles persistence.
 */
const CharacterArcOutputSchema = z.object({
  characterName: z.string().describe(
    'The character\'s name — must match exactly with the corresponding entry in suggestedCharacters.'
  ),
  arcName: z.string().describe(
    'A short, evocative title for the arc (e.g., "From Coward to Hero", "The Price of Ambition").'
  ),
  arcSummary: z.string().describe(
    'A detailed 3-5 sentence summary of the character\'s internal transformation. ' +
    'Captures starting psychological state, key turning points, and resolution. ' +
    'Must be specific about what drives each shift.'
  ),
});

/**
 * Agent output for a plot thread — the hook hydrates with IDs, timestamps, etc.
 */
const PlotThreadOutputSchema = z.object({
  title: z.string().describe(
    'Short, evocative title for the narrative thread (e.g., "The Forgiveness Arc", "The Last Stand"). ' +
    'Should capture the essence of the thread in 3-5 words.'
  ),
  summary: z.string().describe(
    'Context making the question meaningful — what this thread is about and why it matters. ' +
    '2-3 sentences capturing the thread\'s trajectory from introduction to resolution.'
  ),
  threadQuestion: z.string().describe(
    'The dramatic question driving this thread (e.g., "Will she forgive him?", "Can they escape before dawn?"). ' +
    'Must be a genuine question that creates tension and needs resolution within the story.'
  ),
});

/**
 * Output schema for the short-story-planner-v4 agent.
 *
 * Flat structure (3 levels max) for Gemini API compatibility.
 * No storylines or beats — writtenPlot is the single source of truth
 * for the story's structural arc, and plotThreads track dramatic questions.
 */
export const OutputSchema = z.object({
  // Story metadata
  title: z.string().describe('A compelling, evocative title for this short story.'),
  summary: z.string().describe('A concise overview of the plot (50-100 words) that weaves together the story summary, its central theme, and the main conflict.'),

  // Written plot (markdown narrative) — the primary planning artifact
  writtenPlot: z.string().describe(
    'A detailed, multi-paragraph plot outline in Markdown format. ' +
    'Use ## headings for each structural phase (Initial State, Inciting Incident, Break Into Two, Midpoint, Break Into Three, Climax, Final State). ' +
    'Use **bold** for character names and key events, *italic* for emotional states. ' +
    'Separate paragraphs with double newlines. Do NOT use HTML tags. ' +
    'Write in present tense, third person. ' +
    'The narrative should be detailed enough to guide subsequent chapter planning and prose drafting. ' +
    'This is the single source of truth for the story\'s structural arc.'
  ),

  // Plot threads — dramatic questions that drive the narrative
  plotThreads: z.array(PlotThreadOutputSchema).describe(
    'The central and secondary narrative threads driving the story. ' +
    'Include 1 central thread (the main dramatic question) and 1-3 secondary threads. ' +
    'Each thread captures a dramatic question that creates tension and needs resolution. ' +
    'For flash fiction (<3,000 words): 1-2 threads. ' +
    'For standard short fiction (3,000-7,500 words): 2-4 threads.'
  ),

  // Suggested characters
  suggestedCharacters: z.array(CharacterV2IdeaSchema).optional().describe(
    'All relevant characters for the short story (major + minor). ' +
    'Include protagonist, antagonist, and any supporting characters who play a meaningful role. ' +
    'These will be converted to ideated characters for user review.'
  ),

  // Character arcs — short-form summaries (no beats)
  characterArcs: z.array(CharacterArcOutputSchema).optional().describe(
    'The internal psychological transformation arc for each suggested character. ' +
    'Each entry\'s characterName must match a name in suggestedCharacters. ' +
    'For short fiction, arcs are compact summaries — no beat tracking.'
  ),

  // Chapter-level metadata (unified with story planning for short-form)
  pacing: PacingEnum.describe(
    'The overall narrative pacing of the story, derived from the written plot\'s structure and tone.'
  ),
  chapterGoals: ChapterGoalsSchema.describe(
    'The narrative goals this story aims to accomplish — plot advancement, character development, and stakes/tension.'
  ),
  projectedWordCount: z.number().describe(
    'The projected word count for the story based on the detail level of the written plot. ' +
    'Flash fiction: 500-3,000 words. Short story: 3,000-7,500 words.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;

// Type exports for consumer convenience
export type PlotThreadOutput = z.infer<typeof PlotThreadOutputSchema>;
export type CharacterArcOutput = z.infer<typeof CharacterArcOutputSchema>;
